import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMutation } from 'react-query';
import { shippingAPI } from '../services/api';
import { toast } from 'react-toastify';
import { LuX, LuPlus, LuTrash2, LuPackage, LuCheck, LuTriangleAlert, LuChevronDown } from 'react-icons/lu';

const PACKAGE_TYPES = ['box', 'pallet', 'envelope', 'tube', 'crate', 'other'];

/**
 * Package Editor modal for managing packages within a shipment.
 * Lets warehouse staff split shipment line items across packages,
 * set dimensions/weights, and optionally assign serialized assets.
 *
 * Props:
 *   shipment - full shipment object with Packages and LineItems
 *   isOpen - modal visibility
 *   onClose - close callback
 *   onSave - callback after successful save (receives updated shipment)
 */
const PackageEditor = ({ shipment, isOpen, onClose, onSave }) => {
  const [packages, setPackages] = useState([]);
  const [addingItemToPackageIndex, setAddingItemToPackageIndex] = useState(null);
  const [addItemForm, setAddItemForm] = useState({ lineItemId: '', quantity: 1, assetIds: [] });
  const [validationErrors, setValidationErrors] = useState([]);

  // All shipment line items with their SKU info and assets
  const shipmentLineItems = useMemo(() => {
    if (!shipment?.LineItems) return [];
    return shipment.LineItems.map((li) => {
      const requestLine = li.RequestLineItem || {};
      const inventoryItem = requestLine.InventoryItem || {};
      const assets = (li.Assets || []).map((a) => ({
        id: a.inventory_asset_id || a.InventoryAsset?.id,
        serial_number: a.InventoryAsset?.serial_number || null,
        mac_address: a.InventoryAsset?.mac_address || null
      })).filter((a) => a.id);
      return {
        id: li.id,
        sku: inventoryItem.product_sku || requestLine.productSku || 'Unknown',
        productName: inventoryItem.product_name || inventoryItem.description || '',
        quantityToShip: Number(li.quantity_to_ship || 0),
        assets
      };
    });
  }, [shipment?.LineItems]);

  // Initialize packages from existing shipment data
  useEffect(() => {
    if (!isOpen || !shipment) return;

    const existingPackages = shipment.Packages || [];
    if (existingPackages.length > 0) {
      setPackages(existingPackages.map((pkg, idx) => ({
        key: `pkg-${idx}-${Date.now()}`,
        package_type: pkg.package_type || 'box',
        weight_lbs: pkg.weight_lbs ? String(pkg.weight_lbs) : '',
        length: pkg.length ? String(pkg.length) : '',
        width: pkg.width ? String(pkg.width) : '',
        height: pkg.height ? String(pkg.height) : '',
        contents: (pkg.LineItems || []).map((pli) => ({
          shipment_line_item_id: pli.shipment_line_item_id,
          quantity: Number(pli.quantity || 0),
          inventory_asset_id: pli.inventory_asset_id || null
        }))
      })));
    } else {
      // Start with one empty package
      setPackages([makeEmptyPackage()]);
    }
    setValidationErrors([]);
    setAddingItemToPackageIndex(null);
  }, [isOpen, shipment]);

  function makeEmptyPackage() {
    return {
      key: `pkg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      package_type: 'box',
      weight_lbs: '',
      length: '',
      width: '',
      height: '',
      contents: []
    };
  }

  // Compute how many units of each line item are allocated across all packages
  const allocationMap = useMemo(() => {
    const map = new Map();
    for (const li of shipmentLineItems) {
      map.set(li.id, 0);
    }
    for (const pkg of packages) {
      for (const item of pkg.contents) {
        map.set(item.shipment_line_item_id, (map.get(item.shipment_line_item_id) || 0) + item.quantity);
      }
    }
    return map;
  }, [packages, shipmentLineItems]);

  // Track which asset IDs are already allocated
  const allocatedAssetIds = useMemo(() => {
    const set = new Set();
    for (const pkg of packages) {
      for (const item of pkg.contents) {
        if (item.inventory_asset_id) set.add(item.inventory_asset_id);
      }
    }
    return set;
  }, [packages]);

  const getUnassignedQty = useCallback((lineItemId) => {
    const li = shipmentLineItems.find((l) => l.id === lineItemId);
    if (!li) return 0;
    return li.quantityToShip - (allocationMap.get(lineItemId) || 0);
  }, [shipmentLineItems, allocationMap]);

  const getUnassignedAssets = useCallback((lineItemId) => {
    const li = shipmentLineItems.find((l) => l.id === lineItemId);
    if (!li) return [];
    return li.assets.filter((a) => !allocatedAssetIds.has(a.id));
  }, [shipmentLineItems, allocatedAssetIds]);

  // Items that have any unassigned quantity
  const unassignedItems = useMemo(() => {
    return shipmentLineItems
      .map((li) => ({ ...li, unassigned: getUnassignedQty(li.id) }))
      .filter((li) => li.unassigned > 0);
  }, [shipmentLineItems, getUnassignedQty]);

  // Package field updates
  const updatePackageField = (pkgIndex, field, value) => {
    setPackages((prev) => prev.map((pkg, i) => i === pkgIndex ? { ...pkg, [field]: value } : pkg));
  };

  const addPackage = () => {
    setPackages((prev) => [...prev, makeEmptyPackage()]);
  };

  const removePackage = (pkgIndex) => {
    setPackages((prev) => prev.filter((_, i) => i !== pkgIndex));
  };

  const removeContentItem = (pkgIndex, contentIndex) => {
    setPackages((prev) => prev.map((pkg, i) => {
      if (i !== pkgIndex) return pkg;
      return { ...pkg, contents: pkg.contents.filter((_, ci) => ci !== contentIndex) };
    }));
  };

  // Add item flow
  const openAddItemForm = (pkgIndex) => {
    setAddingItemToPackageIndex(pkgIndex);
    const firstAvailable = unassignedItems[0];
    setAddItemForm({
      lineItemId: firstAvailable?.id || '',
      quantity: 1,
      assetIds: []
    });
  };

  const confirmAddItem = () => {
    const pkgIndex = addingItemToPackageIndex;
    if (pkgIndex === null) return;

    const { lineItemId, quantity, assetIds } = addItemForm;
    if (!lineItemId || quantity <= 0) return;

    const maxQty = getUnassignedQty(lineItemId);
    const qty = Math.min(quantity, maxQty);
    if (qty <= 0) return;

    const li = shipmentLineItems.find((l) => l.id === lineItemId);
    const hasSerialized = li && li.assets.length > 0;

    if (hasSerialized && assetIds.length > 0) {
      // Add one content row per asset
      const newContents = assetIds.map((assetId) => ({
        shipment_line_item_id: lineItemId,
        quantity: 1,
        inventory_asset_id: assetId
      }));
      setPackages((prev) => prev.map((pkg, i) => {
        if (i !== pkgIndex) return pkg;
        return { ...pkg, contents: [...pkg.contents, ...newContents] };
      }));
    } else {
      // Add a single content row with the quantity
      setPackages((prev) => prev.map((pkg, i) => {
        if (i !== pkgIndex) return pkg;
        // Merge with existing content for same line item if present (non-serialized)
        const existingIdx = pkg.contents.findIndex(
          (c) => c.shipment_line_item_id === lineItemId && !c.inventory_asset_id
        );
        if (existingIdx >= 0) {
          const updated = [...pkg.contents];
          updated[existingIdx] = {
            ...updated[existingIdx],
            quantity: updated[existingIdx].quantity + qty
          };
          return { ...pkg, contents: updated };
        }
        return {
          ...pkg,
          contents: [...pkg.contents, { shipment_line_item_id: lineItemId, quantity: qty, inventory_asset_id: null }]
        };
      }));
    }

    setAddingItemToPackageIndex(null);
  };

  // Validation
  const validate = useCallback(() => {
    const errors = [];
    if (packages.length === 0) {
      errors.push('At least one package is required.');
      return errors;
    }
    packages.forEach((pkg, i) => {
      const n = i + 1;
      if (!pkg.weight_lbs || Number(pkg.weight_lbs) <= 0) errors.push(`Package ${n}: weight must be greater than 0.`);
      if (!pkg.length || Number(pkg.length) <= 0) errors.push(`Package ${n}: length must be greater than 0.`);
      if (!pkg.width || Number(pkg.width) <= 0) errors.push(`Package ${n}: width must be greater than 0.`);
      if (!pkg.height || Number(pkg.height) <= 0) errors.push(`Package ${n}: height must be greater than 0.`);
      if (pkg.contents.length === 0) errors.push(`Package ${n}: must contain at least one item.`);
    });
    // Check full allocation
    for (const li of shipmentLineItems) {
      const allocated = allocationMap.get(li.id) || 0;
      if (allocated !== li.quantityToShip) {
        errors.push(`${li.sku}: ${allocated}/${li.quantityToShip} allocated. All items must be fully assigned.`);
      }
    }
    return errors;
  }, [packages, shipmentLineItems, allocationMap]);

  // Save mutation
  const saveMutation = useMutation(
    (payload) => shippingAPI.updateShipmentPackages(shipment.id, payload),
    {
      onSuccess: (data) => {
        toast.success('Packages updated');
        onSave?.(data.data?.shipment || data.data);
        onClose();
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to update packages');
      }
    }
  );

  const handleSave = () => {
    const errors = validate();
    setValidationErrors(errors);
    if (errors.length > 0) return;

    const payload = packages.map((pkg) => ({
      package_type: pkg.package_type,
      weight_lbs: Number(pkg.weight_lbs),
      length: Number(pkg.length),
      width: Number(pkg.width),
      height: Number(pkg.height),
      lineItems: pkg.contents.map((c) => ({
        shipment_line_item_id: c.shipment_line_item_id,
        quantity: c.quantity,
        inventory_asset_id: c.inventory_asset_id || null
      }))
    }));

    saveMutation.mutate(payload);
  };

  // Helper to get SKU label for a shipment_line_item_id
  const getSkuLabel = (lineItemId) => {
    const li = shipmentLineItems.find((l) => l.id === lineItemId);
    return li?.sku || 'Unknown';
  };

  const getAssetLabel = (assetId, lineItemId) => {
    const li = shipmentLineItems.find((l) => l.id === lineItemId);
    if (!li) return assetId;
    const asset = li.assets.find((a) => a.id === assetId);
    if (!asset) return String(assetId).slice(0, 8);
    if (asset.serial_number) return `SN ${asset.serial_number}`;
    if (asset.mac_address) return `MAC ${asset.mac_address}`;
    return String(assetId).slice(0, 8);
  };

  if (!isOpen) return null;

  const allFullyAllocated = shipmentLineItems.every(
    (li) => (allocationMap.get(li.id) || 0) === li.quantityToShip
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-base font-semibold text-gray-900 flex items-center">
            <LuPackage className="h-4 w-4 mr-2 text-gray-500" />
            Package Editor
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <LuX className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Unassigned Items */}
          {unassignedItems.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
              <h3 className="text-xs font-semibold text-amber-800 mb-2">Unassigned Items</h3>
              <div className="space-y-1">
                {unassignedItems.map((li) => {
                  const unassignedAssets = getUnassignedAssets(li.id);
                  return (
                    <div key={li.id} className="text-sm text-amber-900">
                      <span className="font-medium">{li.sku}</span>
                      <span className="text-amber-700"> x{li.unassigned}</span>
                      {unassignedAssets.length > 0 && (
                        <span className="text-xs text-amber-600 ml-2">
                          ({unassignedAssets.map((a) => a.serial_number ? `SN ${a.serial_number}` : a.mac_address ? `MAC ${a.mac_address}` : String(a.id).slice(0, 8)).join(', ')})
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Package Cards */}
          {packages.map((pkg, pkgIndex) => (
            <div key={pkg.key} className="rounded-lg border border-gray-200 bg-white">
              {/* Package header */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                <h3 className="text-sm font-semibold text-gray-900">Package {pkgIndex + 1}</h3>
                {packages.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePackage(pkgIndex)}
                    className="text-xs text-red-600 hover:text-red-800 flex items-center"
                  >
                    <LuTrash2 className="h-3 w-3 mr-1" />
                    Delete
                  </button>
                )}
              </div>

              <div className="px-4 py-3 space-y-3">
                {/* Type, Weight, Dims row */}
                <div className="grid grid-cols-5 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Type</label>
                    <select
                      value={pkg.package_type}
                      onChange={(e) => updatePackageField(pkgIndex, 'package_type', e.target.value)}
                      className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      {PACKAGE_TYPES.map((t) => (
                        <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Weight (lbs)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={pkg.weight_lbs}
                      onChange={(e) => updatePackageField(pkgIndex, 'weight_lbs', e.target.value)}
                      className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">L (in)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={pkg.length}
                      onChange={(e) => updatePackageField(pkgIndex, 'length', e.target.value)}
                      className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">W (in)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={pkg.width}
                      onChange={(e) => updatePackageField(pkgIndex, 'width', e.target.value)}
                      className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">H (in)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={pkg.height}
                      onChange={(e) => updatePackageField(pkgIndex, 'height', e.target.value)}
                      className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Contents */}
                <div>
                  <h4 className="text-xs font-medium text-gray-700 mb-2">Contents</h4>
                  {pkg.contents.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No items added yet.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {pkg.contents.map((item, ci) => (
                        <div key={ci} className="flex items-center justify-between rounded border border-gray-100 bg-gray-50 px-3 py-1.5">
                          <div className="text-sm">
                            <span className="font-medium text-gray-900">{getSkuLabel(item.shipment_line_item_id)}</span>
                            <span className="text-gray-500"> x{item.quantity}</span>
                            {item.inventory_asset_id && (
                              <span className="ml-2 text-xs font-mono text-gray-600">
                                {getAssetLabel(item.inventory_asset_id, item.shipment_line_item_id)}
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeContentItem(pkgIndex, ci)}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Items inline form */}
                  {addingItemToPackageIndex === pkgIndex ? (
                    <AddItemInline
                      shipmentLineItems={shipmentLineItems}
                      getUnassignedQty={getUnassignedQty}
                      getUnassignedAssets={getUnassignedAssets}
                      form={addItemForm}
                      setForm={setAddItemForm}
                      onConfirm={confirmAddItem}
                      onCancel={() => setAddingItemToPackageIndex(null)}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => openAddItemForm(pkgIndex)}
                      disabled={unassignedItems.length === 0}
                      className="mt-2 inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      <LuPlus className="h-3 w-3 mr-1" />
                      Add Items
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Add Package button */}
          <button
            type="button"
            onClick={addPackage}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
          >
            <LuPlus className="h-4 w-4 mr-1.5" />
            Add Package
          </button>

          {/* Allocation Summary */}
          {shipmentLineItems.length > 0 && (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
              <h3 className="text-xs font-semibold text-gray-700 mb-2">Allocation Summary</h3>
              <div className="space-y-1">
                {shipmentLineItems.map((li) => {
                  const allocated = allocationMap.get(li.id) || 0;
                  const full = allocated === li.quantityToShip;
                  return (
                    <div key={li.id} className="flex items-center text-sm">
                      {full ? (
                        <LuCheck className="h-3.5 w-3.5 text-green-600 mr-2 flex-shrink-0" />
                      ) : (
                        <LuTriangleAlert className="h-3.5 w-3.5 text-amber-500 mr-2 flex-shrink-0" />
                      )}
                      <span className="font-medium text-gray-900">{li.sku}:</span>
                      <span className={`ml-1 ${full ? 'text-green-700' : 'text-amber-700'}`}>
                        {allocated}/{li.quantityToShip} assigned
                      </span>
                      {!full && (
                        <span className="ml-1 text-amber-600 text-xs">
                          ({li.quantityToShip - allocated} unassigned)
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3">
              <h3 className="text-xs font-semibold text-red-800 mb-1">Please fix the following:</h3>
              <ul className="list-disc list-inside text-sm text-red-700 space-y-0.5">
                {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saveMutation.isLoading || !allFullyAllocated}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {saveMutation.isLoading ? 'Saving...' : 'Save Packages'}
          </button>
        </div>
      </div>
    </div>
  );
};


/**
 * Inline form for adding an item to a package.
 * Handles both non-serialized (pick quantity) and serialized (pick specific assets).
 */
const AddItemInline = ({ shipmentLineItems, getUnassignedQty, getUnassignedAssets, form, setForm, onConfirm, onCancel }) => {
  const availableLineItems = shipmentLineItems.filter((li) => getUnassignedQty(li.id) > 0);
  const selectedLi = shipmentLineItems.find((li) => li.id === form.lineItemId);
  const maxQty = selectedLi ? getUnassignedQty(selectedLi.id) : 0;
  const hasSerialized = selectedLi && selectedLi.assets.length > 0;
  const unassignedAssets = selectedLi ? getUnassignedAssets(selectedLi.id) : [];

  // When line item selection changes, reset quantity and assets
  const handleLineItemChange = (lineItemId) => {
    setForm({ lineItemId, quantity: 1, assetIds: [] });
  };

  const toggleAsset = (assetId) => {
    setForm((prev) => {
      const next = prev.assetIds.includes(assetId)
        ? prev.assetIds.filter((id) => id !== assetId)
        : [...prev.assetIds, assetId];
      return { ...prev, assetIds: next, quantity: next.length || 1 };
    });
  };

  // If serialized and user picks assets, quantity tracks asset count
  const effectiveQty = hasSerialized && form.assetIds.length > 0 ? form.assetIds.length : form.quantity;
  const canConfirm = form.lineItemId && effectiveQty > 0 && effectiveQty <= maxQty;

  return (
    <div className="mt-2 rounded border border-indigo-200 bg-indigo-50 px-3 py-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Item</label>
          <select
            value={form.lineItemId}
            onChange={(e) => handleLineItemChange(e.target.value)}
            className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="">Select item...</option>
            {availableLineItems.map((li) => (
              <option key={li.id} value={li.id}>
                {li.sku} ({getUnassignedQty(li.id)} avail)
              </option>
            ))}
          </select>
        </div>
        {!hasSerialized && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">Quantity (max {maxQty})</label>
            <input
              type="number"
              min="1"
              max={maxQty}
              value={form.quantity}
              onChange={(e) => setForm((prev) => ({ ...prev, quantity: Math.min(Number(e.target.value) || 1, maxQty) }))}
              className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>
        )}
      </div>

      {/* Serialized asset picker */}
      {hasSerialized && unassignedAssets.length > 0 && (
        <div>
          <label className="block text-xs text-gray-600 mb-1">Select Assets ({form.assetIds.length} selected)</label>
          <div className="max-h-32 overflow-y-auto border border-gray-200 rounded bg-white p-2 space-y-1">
            {unassignedAssets.map((asset) => {
              const label = asset.serial_number
                ? `SN ${asset.serial_number}`
                : asset.mac_address
                  ? `MAC ${asset.mac_address}`
                  : String(asset.id).slice(0, 8);
              const checked = form.assetIds.includes(asset.id);
              return (
                <label key={asset.id} className="flex items-center space-x-2 cursor-pointer text-xs text-gray-700 hover:bg-gray-50 rounded px-1 py-0.5">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleAsset(asset.id)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="font-mono">{label}</span>
                </label>
              );
            })}
          </div>
          {form.assetIds.length === 0 && (
            <p className="text-xs text-gray-500 mt-1">
              Or add without specific assets:
              <input
                type="number"
                min="1"
                max={maxQty}
                value={form.quantity}
                onChange={(e) => setForm((prev) => ({ ...prev, quantity: Math.min(Number(e.target.value) || 1, maxQty) }))}
                className="ml-2 w-16 rounded-md border-gray-300 text-xs shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </p>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={onConfirm}
          disabled={!canConfirm}
          className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
        >
          Add
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default PackageEditor;
